import { isClaimOrderLevel, isClaimItemLevel } from '@helpers';
import useFileUploader from '@pulse/composables/useFileUploader';
import { DISPLAY_ORDER_RESOLUTIONS, SalesChannel } from '@pulse/constants/claims';
import ClaimService from '@pulse/services/ClaimService';
import filter from 'lodash/filter';
import map from 'lodash/map';
import { defineStore } from 'pinia';
import { computed, ref, reactive } from 'vue';

import { useOrdersStore } from '~/store/orders';

export const DEFAULT_CLAIM = {
  category: null,
  customer: {},
  fulfillments: {},
  id: '',
  orderId: '',
  resolution: {
    desiredMethod: null,
    updatedShippingAddress: null,
  },
  signature: '',
  sourceOrderId: '',
};

export const useClaimStore = defineStore('conciergeClaim', () => {
  const state = reactive({
    claimSession: null,
  });
  // Had to change claim to a ref so that the subscription in claim-session.js plugin can detect changes with reactive it wasn't picking up every change
  const config = useRuntimeConfig();
  const apiBaseUrl = config.public.apiBaseUrl;
  const claim = ref(DEFAULT_CLAIM);
  const order = useOrdersStore();
  const currentShipmentId = ref(null);

  // Create services - these can be overridden in tests
  let claimService = new ClaimService(apiBaseUrl);
  const { uploadFiles } = useFileUploader(config.public.apiBaseUrl, config.public.storage.googleBucketName);

  const requireCustomerSignature = ref(false);
  const submitClaimOptions = {
    fromValet: false,
    requireCustomerSignature: requireCustomerSignature.value,
  };

  async function findClaimById(id) {
    return claimService.findClaimById(id);
  }

  async function findClaimSession(orderId) {
    const session = await claimService.findClaimSession(orderId);
    state.claimSession = session;

    if (session.metadata?.claim) {
      setClaim(session.metadata.claim);
    }

    if (session.metadata?.currentShipmentId) {
      currentShipmentId.value = session.metadata.currentShipmentId;
    }

    return session;
  }

  async function updateClaimSession(data) {
    const updatedSession = await claimService.updateClaimSession(data);
    state.claimSession = updatedSession;

    if (updatedSession.metadata?.claim && data.metadata?.claim) {
      setClaim(data.metadata.claim);
    }

    if (updatedSession.metadata?.currentShipmentId && data.metadata?.currentShipmentId) {
      currentShipmentId.value = data.metadata.currentShipmentId;
    }

    return updatedSession;
  }

  async function getClaimSubtypes() {
    return claimService.getClaimSubtypes(order.currentOrder.order.storeId);
  }

  async function findReviews(claimId) {
    return claimService.findReviews(claimId);
  }

  async function createReview(review, claimId) {
    return claimService.createReview(review, claimId);
  }

  async function deleteClaimSession() {
    if (!state.claimSession) return;

    await claimService.deleteClaimSession(state.claimSession.id);
    state.claimSession = null;
    claim.value = DEFAULT_CLAIM;
    currentShipmentId.value = null;
  }

  async function sendClaimMessage(content, sourceOrderId, customerId) {
    if (!claim.value._data || !claim.value._data.id) {
      throw new Error('Claim ID not found');
    }

    let medias = [];
    if (content.files.length > 0) {
      medias = await uploadFiles(content.files);
    }

    const claimId = claim.value._data.id;
    const storeId = claim.value._data.storeId;
    const senderName = claim.value._data.authorName;

    const claimMessageInput = {
      claimId,
      medias,
      messageContent: content.messageContent,
      senderId: customerId,
      senderName,
      senderType: 'customer',
      storeId,
    };

    await createClaimMessage(claimMessageInput, claim.value._data.storeId, sourceOrderId);

    const newMessages = await loadClaimMessages(claimId, sourceOrderId);

    claim.value._data.claimMessages = newMessages;
  }

  async function createClaimMessage(data, storeId, sourceOrderId) {
    return claimService.createClaimMessage(data, storeId, sourceOrderId);
  }

  async function loadClaimMessages(claimId, sourceOrderId) {
    return claimService.loadClaimMessages(claimId, sourceOrderId);
  }

  function addFulfillmentIssues(id, issues) {
    const issuesWithIds = issues.map(issue => ({
      ...issue,
      id: Date.now().toString(36) + Math.random().toString(36).substr(2),
    }));

    if (!claim.value.fulfillments[id]) {
      claim.value.fulfillments[id] = {
        id: id,
        issues: issuesWithIds,
      };
    } else {
      /**
       * Remove the issues for the sourceItemId that are already in the store.
       */
      claim.value.fulfillments[id].issues = filter(
        claim.value.fulfillments[id].issues,
        ({ sourceItemId }) => !map(issues, 'sourceItemId').includes(sourceItemId),
      );

      /**
       * Add the new issues for that sourceItemId to the store.
       */
      claim.value.fulfillments[id].issues = claim.value.fulfillments[id].issues.concat(issuesWithIds);
    }
  }

  function setClaim(claimData) {
    claim.value = {
      ...claim.value,
      ...claimData,
    };
  }

  function setFulfillmentState(fulfillments) {
    fulfillments.forEach(id => {
      claim.value.fulfillments[id] = {
        id,
        issues: [],
      };
    });
  }

  function setCurrentShipmentId(id) {
    currentShipmentId.value = id;
  }

  function setRequestedResolution(resolution) {
    claim.value.resolution.desiredMethod = resolution;
  }

  function setClaimCategory(category) {
    claim.value.category = category;
  }

  function setShippingAddress(address) {
    claim.value.resolution.updatedShippingAddress = address;
  }

  function setShipmentIssueType(id, type) {
    const fulfillment = claim.value.fulfillments[id];

    claim.value.fulfillments[id] = {
      ...fulfillment,
      shipmentIssue: type,
    };
  }

  function setShipmentIssueDetails(id, details) {
    claim.value.fulfillments[id].details = details;
  }

  function addSignature(imageUrl) {
    claim.value.signature = imageUrl;
  }

  function saveSignature() {
    const data = {
      claimId: claim.value.id,
      imageUrl: claim.value.signature,
      orderId: claim.value.orderId,
    };

    claimService.saveCustomerSignature(data.claimId, data);
  }

  function deleteIssueByFulfillmentAndIssue(fulfillmentId, issueId) {
    const fulfillment = claim.value.fulfillments[fulfillmentId];
    if (!fulfillment) return;

    fulfillment.issues = fulfillment.issues.filter(issue => issue.id !== issueId);
  }

  function clearIssuesByFulfillment(fulfillmentId) {
    if (claim.value.fulfillments[fulfillmentId]) {
      claim.value.fulfillments[fulfillmentId].issues = [];
    }
  }

  function getIssuesByFulfillmentAndProduct(fulfillmentId, sourceItemId) {
    const fulfillment = claim.value.fulfillments[fulfillmentId];
    if (!fulfillment || !fulfillment.issues) return [];

    return fulfillment.issues.filter(issue => issue.sourceItemId === sourceItemId);
  }

  function transformStoreToClaim(email, claimInfo) {
    const firstFulfillment = Object.values(claimInfo.fulfillments)[0];

    return {
      claimCategory: claim.value.category,
      email,
      fulfillmentIssues: Object.entries(claimInfo.fulfillments).reduce((acc, [sourceFulfillmentId, fulfillment]) => {
        const issues = fulfillment.issues.map(
          // eslint-disable-next-line no-unused-vars
          ({ item: _unusedItem, ...issue }) => ({
            ...issue,
          }),
        );

        acc.push({
          issues,
          sourceFulfillmentId,
        });

        return acc;
      }, []),
      resolution: {
        customerSignature: claimInfo.signature,
        desiredMethod: claimInfo.resolution.desiredMethod,
        updatedShippingAddress: claimInfo.resolution.updatedShippingAddress,
        warrantyPeriod:
          order.currentOrder?.eligibility?.[firstFulfillment.issues[0]?.sourceItemId]?.warranty?.validity ?? null,
      },
      salesChannel: SalesChannel.ONLINE,
    };
  }

  function submitClaim(options = submitClaimOptions) {
    const createClaimData = {
      claim: transformStoreToClaim(claim.value.customer.email, claim.value),
      sourceOrderId: claim.value.customer.sourceOrderId,
      ...claim.value.customer,
    };

    if (options.fromValet) {
      return claimService.createClaimFromValet(createClaimData, options);
    }

    return claimService.createClaim(createClaimData);
  }

  function notifyCustomer(claimId) {
    return claimService.notifyClaimCustomer(claimId);
  }

  function setCustomer({ email, sourceOrderId, customerName }) {
    const customer = {
      customerName,
      email,
      sourceOrderId,
    };

    claim.value = { ...DEFAULT_CLAIM, ...claim.value, customer };
  }

  function reset() {
    claim.value.fulfillments = {};
    claim.value.resolution.updatedShippingAddress = null;
    claim.value.signature = null;
  }

  /**
   * @typedef {Object} FoundationFulfillment
   * @property {string} id
   * @property {Array.<Object>} issues
   *
   * @returns {Array.<FoundationFulfillment>}
   */
  const unfinishedFulfillments = computed(() => {
    return Object.values(claim.value.fulfillments).filter(({ issues }) => issues.length === 0);
  });

  const isOrderLevel = computed(() => {
    return isClaimOrderLevel(claim.value);
  });

  const isItemLevel = computed(() => {
    return isClaimItemLevel(claim.value);
  });

  const claimCategory = computed(() => {
    return claim.value.category;
  });

  const messages = computed(() => {
    if (!claim?.value?._data?.claimMessages) {
      return [];
    }

    const messages = claim?.value?._data?.claimMessages;

    const result = messages.map(message => {
      const formattedDate = new Date(message.createdAt).toLocaleDateString('en-US');

      return {
        ...message,
        date: formattedDate,
        // image: 'https://picsum.photos/200',
        key: message.id,
        medias: message.medias || [],
        message: message.messageContent,
        senderType: message.senderType,
        type: 'message',
        username: message.senderName,
      };
    });

    return result;
  });

  const resolution = computed(() => {
    return claim.value.resolution ? claim.value.resolution : null;
  });

  const currentShipment = computed(() => {
    return claim.value.fulfillments[currentShipmentId.value];
  });

  const currentShipmentIssues = computed(() => {
    return currentShipment.value?.issues || [];
  });

  const allIssues = computed(() => {
    return Object.values(claim.value.fulfillments).reduce((all, { issues }) => all.concat(issues), []);
  });

  const requestedResolution = computed(() => DISPLAY_ORDER_RESOLUTIONS[claim.value.resolution.desiredMethod]);

  const claimSession = computed(() => state.claimSession);

  return {
    addFulfillmentIssues,
    addSignature,
    allIssues,
    claim,
    claimCategory,
    claimService,
    claimSession,
    clearIssuesByFulfillment,
    createClaimMessage,
    createReview,
    currentShipment,
    currentShipmentId,
    currentShipmentIssues,
    deleteClaimSession,
    deleteIssueByFulfillmentAndIssue,
    findClaimById,
    findClaimSession,
    findReviews,
    getClaimSubtypes,
    getIssuesByFulfillmentAndProduct,
    isItemLevel,
    isOrderLevel,
    loadClaimMessages,
    messages,
    notifyCustomer,
    requestedResolution,
    requireCustomerSignature,
    reset,
    resolution,
    saveSignature,
    sendClaimMessage,
    setClaim,
    setClaimCategory,
    setCurrentShipmentId,
    setCustomer,
    setFulfillmentState,
    setRequestedResolution,
    setShipmentIssueDetails,
    setShipmentIssueType,
    setShippingAddress,
    submitClaim,
    unfinishedFulfillments,
    updateClaimSession,
  };
});
